/**
 * Customer Type Demand Chart
 *
 * Displays forecast demand breakdown by customer type.
 * Shows which segments (Grocer, Restaurant, etc.) are driving predicted demand.
 * Supports configurable time periods via period/offset props.
 */

import { useState, useEffect } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { getForecastByPeriod } from '../../services/forecastService'

// Customer type colours - consistent with design system
const CUSTOMER_TYPE_COLOURS = {
  Grocer: '#F54A00',      // Primary orange
  Restaurant: '#009689',  // Teal
  distributor: '#6366F1', // Indigo
  cafe: '#FF8C00',        // Dark orange
  VegBox: '#10B981',      // Emerald
  bakery: '#EC4899',      // Pink
  direct: '#8B5CF6',      // Purple
  Unknown: '#9CA3AF'      // Gray
}

// Custom tooltip with solid background
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <p className="text-sm font-medium mb-2">{data.type}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-muted-foreground">Predicted</span>
            <span className="font-medium">{data.predicted.toFixed(1)}kg</span>
          </div>
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-muted-foreground">Historical</span>
            <span className="font-medium">{data.historical.toFixed(1)}kg</span>
          </div>
          {data.historical > 0 && (
            <div className="flex items-center justify-between gap-4 text-xs">
              <span className="text-muted-foreground">Change</span>
              <span className={`font-medium ${data.predicted >= data.historical ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {data.predicted >= data.historical ? '+' : ''}
                {((data.predicted - data.historical) / data.historical * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
  return null
}

export default function CustomerTypeDemandChart({
  period = '2weeks',
  offset = 0,
  loading: externalLoading
}) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const forecast = await getForecastByPeriod(period, offset)

        // Use byCustomerType data directly
        setData(forecast.byCustomerType || [])
      } catch (error) {
        console.error('Error loading customer type demand:', error)
        setData([])
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [period, offset])

  const isLoading = externalLoading !== undefined ? externalLoading : loading

  if (isLoading) {
    return (
      <div className="reports-chart-container">
        <div className="reports-loading">
          Loading customer type data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="reports-chart-container">
        <div className="reports-loading">
          No customer type data available
        </div>
      </div>
    )
  }

  return (
    <div className="reports-chart-container">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 12,
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{
              value: 'Predicted Volume (kg)',
              position: 'bottom',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
          />
          <YAxis
            dataKey="type"
            type="category"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 12,
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            width={70}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="predicted"
            name="Predicted Volume"
            radius={[0, 4, 4, 0]}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={CUSTOMER_TYPE_COLOURS[entry.type] || CUSTOMER_TYPE_COLOURS.Unknown}
                opacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
